import type { Event as GQLEvent, EventGroup as GQLItem } from '../../../generated/api.graphql';
import { take } from '../../../util';
import * as EventSummary from '../Event/EventSummary';

/**
 * The aggregate returned by `createHolds`. Distinct from `App/Event/EventGroup`, which is the
 * `{id, name}` reference hanging off an `EventSummary` — same schema type, two projections
 * (see api-layer.md, "multiple models per domain").
 */
export type EventGroup = {
  id: number;
  name: string;
  events: EventSummary.EventSummary[];
};

/**
 * `HOLD_EVENT_FIELDS` selects only fields that exist on both `Event` and `EventSummary` with
 * identical types (verified field-by-field, including `state`'s enum). `Event` is a strict
 * superset — it also carries `presales`, `ticketSales`, `availableFor*` and others — so the cast
 * is sound because TS assignability ignores extra properties; `__typename` is the one genuine
 * conflict, which `Omit` strips. Reuse the single canonical mapper rather than cloning its
 * 20-field body, which would silently drift.
 */
const eventToSummary = (item: GQLEvent): EventSummary.EventSummary =>
  EventSummary.fromGql(item as Omit<GQLEvent, '__typename'>);

export const fromGql = (item: GQLItem): EventGroup => ({
  ...take<EventGroup>(item, 'id', 'name'),
  events: item.events.map(eventToSummary),
});
